import { ButtonBase, Grid, useTheme } from "@mui/material";
import React, { useEffect, useState, CSSProperties } from "react";
import BaseEntity from "../../models/BaseEntity";
import { ThemeColors } from "../../Theme/AppTheme";
import hexToRGB from "../../utils/hexToRGB";
import { sxClsx } from "utils/styleClsx";

export enum AnswerColor {
    RED = "red",
    YELLOW = "yellow",
    GREEN = "green",
    GRAY = "gray",
    BLUE = "blue",
    DARKBLUE = "darkblue",
}

export interface AnswerSelection {
    text: string;
    color: AnswerColor;
}

interface Props<T> {
    options: Array<{ data: T; option: AnswerSelection }>;
    onSelect: (arg0: T) => void;
    defaultValue?: T;
    buttonRootStyles?: CSSProperties;
    isDisabled?: boolean;
    isConvoWorkflow?: boolean;
}

function AcxAnswerSelector<T extends BaseEntity>(props: Props<T>) {
    const [selected, setSelected] = useState<number>();
    const theme = useTheme();

    const colors = {
        green: {
            color: theme.palette.black.main,
            borderColor: theme.palette.green.main,
            backgroundColor: theme.palette.green.background,
        },
        yellow: {
            color: theme.palette.black.main,
            borderColor: theme.palette.yellow.main,
            backgroundColor: theme.palette.yellow.background,
        },
        red: {
            color: theme.palette.black.main,
            borderColor: theme.palette.red.main,
            backgroundColor: theme.palette.red.background,
        },
        blue: {
            color: theme.palette.black.main,
            borderColor: theme.palette.info.main,
            backgroundColor: hexToRGB(theme.palette.info.main, 0.05),
        },
        darkblue: {
            color: theme.palette.black.main,
            borderColor: theme.palette.black.main,
            backgroundColor: ThemeColors.lightBlue,
        },
    };

    useEffect(() => {
        if (props.defaultValue !== undefined) {
            const defaultValueIndex = props.options.findIndex(
                (value) => value.data.id === props.defaultValue?.id,
            );
            setSelected(defaultValueIndex);
        } else {
            setSelected(-1);
        }
    }, [props.defaultValue, props.options]);

    function clickHandler(optionIndex: number) {
        if (!props.isDisabled) {
            if (optionIndex === selected) {
                setSelected(-1);
                // @ts-ignore
                props.onSelect(undefined);
            } else {
                setSelected(optionIndex);
                const option = props.options[optionIndex];
                props.onSelect(option.data);
            }
        }
    }

    function renderOption(option: AnswerSelection, optionIndex: number) {
        return (
            <Grid
                item
                xs={12}
                lg={props.isConvoWorkflow ? false : true}
                xl={props.isConvoWorkflow ? true : false}
                sx={sxClsx(
                    {
                        backgroundColor: theme.palette.white.main,
                        color: theme.palette.text.disabled,
                        borderColor: hexToRGB(theme.palette.gray.main, 0.3),
                        fontFamily: "Inter",
                        fontSize: "14px",
                        letterSpacing: 0,
                        // lineHeight: "16px",
                        cursor: "pointer",
                        userSelect: "none",
                        borderStyle: "solid",
                        borderWidth: "1px",
                        textAlign: "center",
                        fontWeight: "bolder",
                        "&:hover": {
                            color: theme.palette.blue.main,
                            outlineColor: theme.palette.blue.main,
                            backgroundColor: theme.palette.blue.background,
                        },
                    },
                    optionIndex === selected && {
                        ...colors[option.color.valueOf()],
                        cursor: "pointer",
                        fontWeight: "normal",
                        "&:hover": {
                            ...colors[option.color.valueOf()],
                        },
                    },
                )}
                style={{
                    minHeight: "28px",
                    marginRight: "8px",
                    borderRadius: "5px",
                    marginBottom: "0.5rem",
                    ...props.buttonRootStyles,
                }}
                key={`option_${optionIndex}`}
            >
                <ButtonBase
                    sx={sxClsx(
                        {
                            backgroundColor: "inherit",
                            userSelect: "none",
                            height: "100%",
                            outlineColor: "inherit",
                            fontFamily: "inherit",
                            letterSpacing: "inherit",
                            lineHeight: "inherit",
                            fontSize: "inherit",
                            outlineStyle: "inherit",
                            outlineWidth: "inherit",
                            textAlign: "center",
                            fontWeight: "bold",
                            width: "100%",
                            textTransform: "none",
                            paddingTop: "2px",
                            paddingBottom: "2px",
                            paddingLeft: "3px",
                            paddingRight: "3px",
                            borderRadius: "5px",
                        },
                        props.isConvoWorkflow && { textWrap: "wrap" },
                    )}
                    onClick={() => clickHandler(optionIndex)}
                >
                    {option.text}
                </ButtonBase>
            </Grid>
        );
    }

    return (
        <Grid
            container
            sx={{ boxSizing: "border-box", width: "100%", height: "100%" }}
        >
            {props.options.map((value) => value.option).map(renderOption)}
        </Grid>
    );
}

export default AcxAnswerSelector;
